// Daos Ads /planner — shared constants + helpers (render, upload, export).
import * as XLSX from "xlsx";
import { FORMAT_OPTIONS, TEMPLATE_OPTIONS, renderTemplate, loadImage } from "../studio/canvas";

export const NETWORKS = [
  { id: "ig-feed", label: "Instagram Feed" },
  { id: "ig-reels", label: "Instagram Reels" },
  { id: "tiktok", label: "TikTok" },
  { id: "facebook", label: "Facebook" },
];

export const OBJECTIVES = [
  { id: "leads", label: "Generar leads" },
  { id: "awareness", label: "Reconocimiento" },
  { id: "autoridad", label: "Autoridad / marca personal" },
  { id: "ventas", label: "Ventas directas" },
];

export const BACKGROUND_MODES = [
  { id: "mixed", label: "Mixto (IA + tus assets)" },
  { id: "ai", label: "Solo fondos con IA" },
  { id: "upload", label: "Solo mis assets" },
];

export const VIDEO_FORMATS = [
  { id: "reels", label: "Reels · 1080×1920" },
  { id: "feed", label: "Feed · 1080×1080" },
  { id: "tiktok", label: "TikTok · 1080×1920" },
];

export const ART_FORMATS = FORMAT_OPTIONS;
export const ART_TEMPLATES = TEMPLATE_OPTIONS;

export const STATUS_META = {
  pending: { label: "Pendiente", cls: "text-zinc-300 border-white/15 bg-white/5" },
  approved: { label: "Aprobada", cls: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  rejected: { label: "Descartada", cls: "text-rose-300 border-rose-500/30 bg-rose-500/10" },
};

export function networkLabel(id) {
  return NETWORKS.find((n) => n.id === id)?.label || id;
}

export function artFormat(id) {
  return ART_FORMATS.find((f) => f.id === id) || ART_FORMATS[0];
}

export function aspectOf(formatId) {
  return artFormat(formatId).aspect;
}

// Downscale an uploaded image to a max long side and return a JPEG data URL.
export async function downscaleImage(file, maxSide = 1280, quality = 0.74) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await loadImage(dataUrl);
  const long = Math.max(img.naturalWidth, img.naturalHeight);
  if (long <= maxSide) return dataUrl;
  const s = maxSide / long;
  const c = document.createElement("canvas");
  c.width = Math.round(img.naturalWidth * s);
  c.height = Math.round(img.naturalHeight * s);
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", quality);
}

function phrases(raw) {
  return String(raw || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

// Render a static art piece with the /studio canvas engine → PNG data URL.
export async function renderArtPiece(piece, { backgroundDataUrl, logo }) {
  const fmt = artFormat(piece.format);
  const canvas = document.createElement("canvas");
  canvas.width = fmt.width;
  canvas.height = fmt.height;
  const ctx = canvas.getContext("2d");
  let bg = null;
  if (backgroundDataUrl) {
    try { bg = await loadImage(backgroundDataUrl); } catch (e) { bg = null; }
  }
  await renderTemplate(ctx, {
    width: fmt.width,
    height: fmt.height,
    templateId: piece.template,
    content: {
      headline: piece.headline || "",
      subtitle: piece.subtitle || "",
      cta: piece.cta || "",
      author: "David Osorio",
    },
    background: bg,
    logo,
    gradientWords: phrases(piece.gradient_words),
    signalWords: phrases(piece.signal_words),
    signalColor: piece.signal_color || null,
    ctaColor: piece.cta_color || "gradient",
  });
  return canvas.toDataURL("image/png");
}

export function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function exportPlanXlsx(pieces, planName = "plan-daos-ads") {
  const rows = pieces.map((p) => ({
    Fecha: p.date,
    Red: networkLabel(p.network),
    Tipo: p.type === "video" ? "Video" : "Arte",
    Formato: p.format,
    Plantilla: p.template,
    "Duración (s)": p.duration || "",
    Bajada: p.eyebrow || "",
    Titular: p.headline || "",
    Subtítulo: p.subtitle || "",
    CTA: p.cta || "",
    "Palabras degradado": p.gradient_words || "",
    "Palabras señal": p.signal_words || "",
    Fondo: p.background_source === "upload" ? "Asset propio" : "IA",
    "Prompt fondo": p.background_prompt || "",
    Estado: STATUS_META[p.status]?.label || p.status,
    Notas: p.notes || "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = Object.keys(rows[0] || { a: 1 }).map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Plan de contenido");
  XLSX.writeFile(wb, `${planName.replace(/[^\w\-]+/g, "-").toLowerCase() || "plan"}.xlsx`);
}

// Hand off a piece to /studio or /studiovideo through sessionStorage.
export const PREFILL_KEY = "daos_planner_prefill";

export function setPrefill(payload) {
  try {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify(payload));
  } catch (e) { /* quota — ignore */ }
}

export function readPrefill() {
  try {
    const raw = sessionStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PREFILL_KEY);
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
